import type { AuctionListing, AuctionMenuSummary, AuctionNotification } from '../shared/auction-types.js';
import type { CardDef, AbilityType, Rarity } from '../types.js';
import type { Target } from '../shared/targeting.js';
import type { MenuAction } from './MenuView.js';
import { apiClient } from '../api-client.js';
import { formatCountdown } from './Countdown.js';
import { showNotice } from './NoticeView.js';
import { createCardElement } from './CardView.js';
import { mergeMissingCardsByIds, listCardForAuction, type PlayerData } from '../collection/PlayerState.js';
import { isCustomCardId } from './Share.js';
import {
  RARITY_ORDER, RARITY_ORDER_LIST, RARITY_LABEL,
  getCardPower, getCardSpirit, getCardClock,
  getCardEffect, getCardAbilityType, getCardTarget,
  ABILITY_TYPE_ORDER, ABILITY_TYPE_LABEL, TARGET_ORDER, TARGET_LABEL,
  effectLabel,
} from './CardSort.js';
import { positionDropdown } from './DropdownPosition.js';

const AUCTION_LISTING_TICKET_ID = 'auction-listing';

type SortKey = 'activity' | 'price' | 'name' | 'rarity' | 'power' | 'spirit' | 'clock';
type SortDir = 'asc' | 'desc';

const SORT_LABELS: Record<SortKey, string> = {
  activity: 'Activity',
  price: 'Price',
  name: 'Name',
  rarity: 'Rarity',
  power: 'Power',
  spirit: 'Spirit',
  clock: 'Clock',
};

/** Natural direction per sort key — the ordering a player expects when
 *  they first pick that sort. Activity descends (newest first), price /
 *  clock ascend (cheap / fast first), etc. The direction toggle flips
 *  this baseline. */
const DEFAULT_SORT_DIR: Record<SortKey, SortDir> = {
  activity: 'desc',
  price: 'asc',
  name: 'asc',
  rarity: 'asc',   // legendary first (RARITY_ORDER: legendary=0)
  power: 'desc',
  spirit: 'desc',
  clock: 'asc',
};

// The browse view: filter chips + card-tile grid using the same
// `createCardElement` visuals as the deck editor, plus an overlay
// footer with price / countdown / my-bid chip. On mount we drain any
// unread notifications (the pip driver) by showing them as popups in
// order, then POST /notifications/read to clear the badge.
//
// Returns a teardown callback for the router to invoke on navigation:
// stops the countdown ticker and detaches document-level listeners.

export type AuctionsFilter = 'all' | 'leading' | 'outbid' | 'my-sales' | 'pending-claims';

interface ViewState {
  listings: AuctionListing[];
  pendingClaims: AuctionListing[];
  filter: AuctionsFilter;
  summary: AuctionMenuSummary | null;
  sort: { key: SortKey; dir: SortDir };
  // Filter sets are initialized to include every value present in the
  // current listing set. When the user unchecks something, that value
  // drops out and any card carrying it is filtered out. Cards missing
  // the property entirely (e.g. an action card has no trigger type)
  // pass through when the filter is at its full "everything checked"
  // state and are excluded once the user starts narrowing — matches
  // the deck editor's behavior so the two views feel consistent.
  filterRarities: Set<Rarity>;
  filterTags: Set<string>;
  filterEffects: Set<string>;
  filterTriggers: Set<AbilityType>;
  filterTargets: Set<Target>;
}

export function createAuctionHouseView(
  container: HTMLElement,
  catalog: Map<string, CardDef>,
  playerData: PlayerData,
  onAction: (action: MenuAction) => void,
  onSelect: (auctionId: string) => void,
  onGemsChanged: (gems: number) => void,
): () => void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen auction-house is-scrollable';
  const ticketCount = () => playerData.tickets[AUCTION_LISTING_TICKET_ID]?.count ?? 0;
  // The .player-gems class is the shared "current gem balance"
  // selector — main.ts patches every instance on the page via
  // querySelectorAll after any spend/earn, so a fresh bid on this
  // page updates the readout here automatically. The ticket count
  // updates locally when listCardForAuction runs.
  // Sticky header — Back on the left, title centered, sell + ticket
  // count + gems on the right. Mirrors the tournament-sticky-header
  // pattern so the layout reads consistently across scrollable
  // detail-style views.
  el.innerHTML = `
    <div class="auction-sticky-header">
      <button class="menu-btn-sm auction-sticky-back" id="back-btn">&larr; Back</button>
      <h1 class="auction-sticky-title">Auction House</h1>
      <div class="auction-sticky-actions">
        <button class="menu-btn-sm auction-sell-btn" id="sell-btn">Sell a Card</button>
        <span class="auction-ticket-count" id="ticket-count">${ticketCount()} 🎟</span>
        <span class="player-gems">${playerData.gems} gems</span>
      </div>
    </div>
    <div class="auction-summary-line meta"></div>
    <div class="filter-chips" id="auction-filters"></div>
    <div class="auction-sort-bar" id="auction-sort-bar"></div>
    <div class="auction-tiles" id="auction-tiles">
      <div class="auction-loading meta">Loading auctions…</div>
    </div>
  `;

  const sellBtn = el.querySelector('#sell-btn') as HTMLButtonElement;
  const ticketEl = el.querySelector('#ticket-count') as HTMLElement;
  const refreshSellBtnState = (): void => {
    const count = ticketCount();
    ticketEl.textContent = `${count} 🎟`;
    ticketEl.title = count === 1 ? '1 auction ticket' : `${count} auction tickets`;
    if (count > 0) {
      sellBtn.classList.remove('disabled');
    } else {
      sellBtn.classList.add('disabled');
    }
  };
  refreshSellBtnState();
  sellBtn.addEventListener('click', () => {
    if (ticketCount() === 0) {
      // Not truly `disabled` on the DOM — we need the click to fire
      // so the user gets a hint. Tooltip-on-tap is the standard mobile
      // affordance for "why is this greyed out".
      showNotice({
        title: 'No auction tickets',
        message: 'Earn tickets by placing in tournaments.',
      });
      return;
    }
    void openAuctionSellPicker({
      playerData,
      catalog,
      onListed: () => {
        refreshSellBtnState();
        renderSummaryLine(el, playerData.auctionSummary);
        // Re-fetch listings so the seller's new active-sale shows up
        // in the "My sales" filter without a manual reload.
        void refetchListings();
      },
    });
  });
  container.appendChild(el);

  el.querySelector('#back-btn')!.addEventListener('click', () => onAction({ type: 'back-to-menu' }));

  const state: ViewState = {
    listings: [],
    pendingClaims: [],
    filter: 'all',
    summary: null,
    sort: { key: 'activity', dir: 'desc' },
    filterRarities: new Set<Rarity>(),
    filterTags: new Set<string>(),
    filterEffects: new Set<string>(),
    filterTriggers: new Set<AbilityType>(),
    filterTargets: new Set<Target>(),
  };
  // Snapshot of the sorted/deduped values pulled from the current
  // listing set — used to render dropdown items and to detect
  // "everything checked" (which passes-through cards missing the
  // property). Recomputed whenever listings change.
  let availableRarities: Rarity[] = [];
  let availableTags: string[] = [];
  let availableEffects: string[] = [];
  let availableTriggers: AbilityType[] = [];
  let availableTargets: Target[] = [];
  let countdownTimer: ReturnType<typeof setInterval> | null = null;

  const renderFilters = (): void => {
    const chips = el.querySelector('#auction-filters')!;
    chips.innerHTML = '';
    const options: { id: AuctionsFilter; label: string }[] = [
      { id: 'all', label: 'All' },
      { id: 'leading', label: 'Leading' },
      { id: 'outbid', label: 'Outbid' },
      { id: 'my-sales', label: 'My sales' },
      { id: 'pending-claims', label: 'To claim' },
    ];
    for (const opt of options) {
      const btn = document.createElement('button');
      btn.className = `menu-btn-sm auction-filter-chip${state.filter === opt.id ? ' active' : ''}`;
      btn.textContent = opt.label;
      btn.addEventListener('click', () => {
        state.filter = opt.id;
        renderFilters();
        renderTiles();
      });
      chips.appendChild(btn);
    }
  };

  /** Refresh the union sets of rarity / tag / effect / trigger / target
   *  from the current listings. Called whenever `state.listings` /
   *  `state.pendingClaims` change. Values are ordered by their canonical
   *  taxonomy (RARITY_ORDER_LIST, TARGET_ORDER, ...) so the dropdowns
   *  read predictably across sessions. Filter sets seed to "everything
   *  present" so no filter is active by default; if the population
   *  shrinks (e.g. a listing closes), we drop absent values from the
   *  filter set so a stale check doesn't over-narrow. */
  const refreshFilterUniverse = (): void => {
    const rar = new Set<Rarity>();
    const tags = new Set<string>();
    const effs = new Set<string>();
    const trigs = new Set<AbilityType>();
    const tgts = new Set<Target>();
    for (const l of [...state.listings, ...state.pendingClaims]) {
      const def = catalog.get(l.cardId);
      if (!def) continue;
      rar.add(def.rarity);
      for (const t of def.tags) tags.add(t);
      const e = getCardEffect(def);
      if (e) effs.add(e);
      const at = getCardAbilityType(def);
      if (at) trigs.add(at);
      const tg = getCardTarget(def);
      if (tg) tgts.add(tg);
    }
    availableRarities = RARITY_ORDER_LIST.filter(r => rar.has(r as Rarity)) as Rarity[];
    availableTags = [...tags].sort();
    availableEffects = [...effs].sort((a, b) => effectLabel(a).localeCompare(effectLabel(b)));
    availableTriggers = ABILITY_TYPE_ORDER.filter(t => trigs.has(t));
    availableTargets = TARGET_ORDER.filter(t => tgts.has(t));
    // Seed to "everything" on first population; otherwise preserve
    // user choices while dropping values that no longer exist.
    if (state.filterRarities.size === 0) state.filterRarities = new Set(availableRarities);
    else for (const r of [...state.filterRarities]) if (!rar.has(r)) state.filterRarities.delete(r);
    if (state.filterTags.size === 0) state.filterTags = new Set(availableTags);
    else for (const t of [...state.filterTags]) if (!tags.has(t)) state.filterTags.delete(t);
    if (state.filterEffects.size === 0) state.filterEffects = new Set(availableEffects);
    else for (const e of [...state.filterEffects]) if (!effs.has(e)) state.filterEffects.delete(e);
    if (state.filterTriggers.size === 0) state.filterTriggers = new Set(availableTriggers);
    else for (const t of [...state.filterTriggers]) if (!trigs.has(t)) state.filterTriggers.delete(t);
    if (state.filterTargets.size === 0) state.filterTargets = new Set(availableTargets);
    else for (const t of [...state.filterTargets]) if (!tgts.has(t)) state.filterTargets.delete(t);
  };

  /** Build a checkbox-list dropdown filter (used for tags / abilities /
   *  triggers / targets). Reuses .tag-filter-* CSS from the deck editor.
   *  `activeSet` is mutated in place on toggle. `onChange` refreshes
   *  the dropdown label + re-renders the tiles list. */
  function buildFilterDropdown<T extends string>(opts: {
    label: string;
    values: readonly T[];
    activeSet: Set<T>;
    labelFor: (v: T) => string;
    onChange: () => void;
  }): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'tag-filter-dropdown';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tag-filter-btn';
    const menu = document.createElement('div');
    menu.className = 'tag-filter-menu';
    wrap.appendChild(btn);
    wrap.appendChild(menu);

    const rerenderLabel = (): void => {
      const all = opts.activeSet.size === opts.values.length;
      btn.textContent = all ? `${opts.label} ▾` : `${opts.label} (${opts.activeSet.size}) ▾`;
    };
    const rerenderMenu = (): void => {
      const allChecked = opts.activeSet.size === opts.values.length;
      menu.innerHTML = `
        <label class="tag-filter-item">
          <input type="checkbox" data-key="__all__" ${allChecked ? 'checked' : ''} /> All
        </label>
        ${opts.values.map(v => `
          <label class="tag-filter-item">
            <input type="checkbox" data-key="${escapeAttr(v)}" ${opts.activeSet.has(v) ? 'checked' : ''} /> ${escapeHtml(opts.labelFor(v))}
          </label>
        `).join('')}
      `;
      menu.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
          const key = (cb as HTMLInputElement).dataset.key!;
          if (key === '__all__') {
            if ((cb as HTMLInputElement).checked) {
              for (const v of opts.values) opts.activeSet.add(v);
            } else {
              opts.activeSet.clear();
            }
          } else {
            const v = key as T;
            if ((cb as HTMLInputElement).checked) opts.activeSet.add(v);
            else opts.activeSet.delete(v);
          }
          rerenderMenu();
          rerenderLabel();
          opts.onChange();
        });
      });
    };
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const wasOpen = menu.classList.contains('open');
      // Close any other filter dropdown before opening this one.
      el.querySelectorAll('.tag-filter-menu.open').forEach(m => m.classList.remove('open'));
      if (!wasOpen) {
        menu.classList.add('open');
        // On narrow viewports the trigger button may sit near the
        // left edge, which would push the right-anchored menu
        // partially off-screen. Nudge back into view.
        positionDropdown(menu);
      }
    });
    menu.addEventListener('click', (e) => e.stopPropagation());

    rerenderLabel();
    rerenderMenu();
    return wrap;
  }

  const renderSortBar = (): void => {
    const bar = el.querySelector('#auction-sort-bar')!;
    bar.innerHTML = '';

    // Sort key select
    const sortWrap = document.createElement('label');
    sortWrap.className = 'auction-sort-wrap';
    sortWrap.innerHTML = '<span class="auction-sort-label">Sort:</span>';
    const sortSelect = document.createElement('select');
    sortSelect.className = 'auction-sort-select';
    const keys: SortKey[] = ['activity', 'price', 'name', 'rarity', 'power', 'spirit', 'clock'];
    for (const k of keys) {
      const opt = document.createElement('option');
      opt.value = k;
      opt.textContent = SORT_LABELS[k];
      if (state.sort.key === k) opt.selected = true;
      sortSelect.appendChild(opt);
    }
    sortSelect.addEventListener('change', () => {
      const newKey = sortSelect.value as SortKey;
      // Snapping direction to the new key's natural default is the
      // less-surprising behavior: picking "Price" first shows cheap
      // first, picking "Power" shows strongest first. User can flip
      // afterward if they want the opposite.
      state.sort = { key: newKey, dir: DEFAULT_SORT_DIR[newKey] };
      renderSortBar();
      renderTiles();
    });
    sortWrap.appendChild(sortSelect);
    bar.appendChild(sortWrap);

    // Direction toggle
    const dirBtn = document.createElement('button');
    dirBtn.type = 'button';
    dirBtn.className = 'auction-sort-dir';
    dirBtn.textContent = state.sort.dir === 'asc' ? '▲' : '▼';
    dirBtn.title = state.sort.dir === 'asc' ? 'Ascending (click to flip)' : 'Descending (click to flip)';
    dirBtn.addEventListener('click', () => {
      state.sort = { key: state.sort.key, dir: state.sort.dir === 'asc' ? 'desc' : 'asc' };
      renderSortBar();
      renderTiles();
    });
    bar.appendChild(dirBtn);

    // Filter dropdowns
    bar.appendChild(buildFilterDropdown<Rarity>({
      label: 'Rarity',
      values: availableRarities,
      activeSet: state.filterRarities,
      labelFor: (r) => RARITY_LABEL[r] ?? r,
      onChange: renderTiles,
    }));
    bar.appendChild(buildFilterDropdown<string>({
      label: 'Tags',
      values: availableTags,
      activeSet: state.filterTags,
      labelFor: (t) => t,
      onChange: renderTiles,
    }));
    bar.appendChild(buildFilterDropdown<string>({
      label: 'Abilities',
      values: availableEffects,
      activeSet: state.filterEffects,
      labelFor: effectLabel,
      onChange: renderTiles,
    }));
    bar.appendChild(buildFilterDropdown<AbilityType>({
      label: 'Triggers',
      values: availableTriggers,
      activeSet: state.filterTriggers,
      labelFor: (t) => ABILITY_TYPE_LABEL[t],
      onChange: renderTiles,
    }));
    bar.appendChild(buildFilterDropdown<Target>({
      label: 'Targets',
      values: availableTargets,
      activeSet: state.filterTargets,
      labelFor: (t) => TARGET_LABEL[t],
      onChange: renderTiles,
    }));
  };

  // Close any open filter dropdown on outside click. Captured on the
  // handler variable so the teardown callback can detach it.
  const outsideClickHandler = (): void => {
    el.querySelectorAll('.tag-filter-menu.open').forEach(m => m.classList.remove('open'));
  };
  document.addEventListener('click', outsideClickHandler);

  /** Base list the bid-state chip narrows over. Pending claims come
   *  from their own server-side array (won-but-unclaimed) because the
   *  browseable `listings` are active-only. The "All" filter surfaces
   *  them at the top since they're the highest-priority actionable
   *  items for the player. */
  const applyBidStateFilter = (): AuctionListing[] => {
    switch (state.filter) {
      case 'leading':        return state.listings.filter(l => l.myBidState === 'leading');
      case 'outbid':         return state.listings.filter(l => l.myBidState === 'outbid');
      case 'my-sales':       return state.listings.filter(l => !l.isSystemSeller);
      case 'pending-claims': return state.pendingClaims;
      default:               return [...state.pendingClaims, ...state.listings];
    }
  };

  /** Second-pass card-property filters (rarity, tags, ability slots).
   *  Cards missing a filterable property (e.g. an action card has no
   *  trigger) pass through when that filter is at its "all checked"
   *  baseline and are dropped once the user narrows the set — matches
   *  the deck editor's behavior. */
  const applyCardFilters = (list: AuctionListing[]): AuctionListing[] => {
    const allRarities = state.filterRarities.size === availableRarities.length;
    const allTags = state.filterTags.size === availableTags.length;
    const allEffects = state.filterEffects.size === availableEffects.length;
    const allTriggers = state.filterTriggers.size === availableTriggers.length;
    const allTargets = state.filterTargets.size === availableTargets.length;
    return list.filter(l => {
      const def = catalog.get(l.cardId);
      if (!def) return true;  // no def yet — don't hide it
      if (!allRarities && !state.filterRarities.has(def.rarity)) return false;
      if (!allTags) {
        if (!def.tags.some(t => state.filterTags.has(t))) return false;
      }
      if (!allEffects) {
        const eff = getCardEffect(def);
        if (!eff || !state.filterEffects.has(eff)) return false;
      }
      if (!allTriggers) {
        const trig = getCardAbilityType(def);
        if (!trig || !state.filterTriggers.has(trig)) return false;
      }
      if (!allTargets) {
        const tgt = getCardTarget(def);
        if (!tgt || !state.filterTargets.has(tgt)) return false;
      }
      return true;
    });
  };

  /** Comparator for the selected sort. Returns positive when `a`
   *  should come AFTER `b` in the natural direction (as declared in
   *  DEFAULT_SORT_DIR). `applySort` reverses the sign when the user
   *  has flipped the direction toggle. */
  const compareByNaturalDir = (a: AuctionListing, b: AuctionListing): number => {
    const defA = catalog.get(a.cardId);
    const defB = catalog.get(b.cardId);
    switch (state.sort.key) {
      case 'activity':
        // Natural = desc (newest first) — larger ISO string sorts before smaller.
        return b.lastActivityAt.localeCompare(a.lastActivityAt);
      case 'price':
        // Natural = asc (cheap first).
        return a.visiblePrice - b.visiblePrice;
      case 'name':
        return (defA?.name ?? a.cardName).localeCompare(defB?.name ?? b.cardName);
      case 'rarity': {
        const ra = RARITY_ORDER[defA?.rarity ?? a.cardRarity] ?? 9;
        const rb = RARITY_ORDER[defB?.rarity ?? b.cardRarity] ?? 9;
        return ra - rb;
      }
      case 'power':
        // Natural = desc (highest first) — biggest number first.
        return (defB ? getCardPower(defB) : 0) - (defA ? getCardPower(defA) : 0);
      case 'spirit':
        return (defB ? getCardSpirit(defB) : 0) - (defA ? getCardSpirit(defA) : 0);
      case 'clock':
        return (defA ? getCardClock(defA) : 1) - (defB ? getCardClock(defB) : 1);
    }
  };

  const applySort = (list: AuctionListing[]): AuctionListing[] => {
    const natural = DEFAULT_SORT_DIR[state.sort.key];
    const flip = state.sort.dir !== natural;
    return [...list].sort((a, b) => {
      let cmp = compareByNaturalDir(a, b);
      if (flip) cmp = -cmp;
      // Stable secondary tie-break by name so equal-key rows have a
      // predictable order across renders.
      if (cmp === 0) cmp = a.cardName.localeCompare(b.cardName);
      return cmp;
    });
  };

  const renderTiles = (): void => {
    const tiles = el.querySelector('#auction-tiles')!;
    tiles.innerHTML = '';
    const filtered = applySort(applyCardFilters(applyBidStateFilter()));
    if (filtered.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'auction-empty meta';
      empty.textContent = state.filter === 'pending-claims'
        ? 'No auctions are awaiting your claim right now.'
        : state.filter === 'all'
          ? 'No active auctions. Check back tomorrow for more listings!'
          : 'Nothing matches this filter.';
      tiles.appendChild(empty);
      return;
    }
    for (const l of filtered) {
      tiles.appendChild(buildTile(l, catalog, onSelect));
    }
  };

  // Countdown ticker: every second walk the visible tiles and update
  // their .auction-tile-countdown text. Cheap even at hundreds of tiles.
  countdownTimer = setInterval(() => {
    const cells = el.querySelectorAll<HTMLElement>('.auction-tile-countdown');
    const now = Date.now();
    cells.forEach(c => {
      const ends = parseInt(c.dataset.endsAt ?? '0', 10);
      c.textContent = formatCountdown(ends, now);
    });
  }, 1000);

  // Fetch listings + pending claims + fresh gems. Extracted so the
  // sell flow can re-invoke it after a new listing without re-mounting
  // the entire view (mount runs notifications drain, which we don't
  // want to repeat).
  const refetchListings = async (): Promise<void> => {
    try {
      const { listings, pendingClaims, gems: freshGems } = await apiClient.listAuctions();
      onGemsChanged(freshGems);
      const allCardIds = [...listings, ...pendingClaims].map(l => l.cardId);
      await mergeMissingCardsByIds(catalog, allCardIds);
      state.listings = listings;
      state.pendingClaims = pendingClaims;
      refreshFilterUniverse();
      renderFilters();
      renderSortBar();
      renderTiles();
    } catch (err) {
      console.error('Failed to refresh auctions', err);
    }
  };

  // Refresh the summary and mirror it onto playerData so the main-menu
  // widget picks up the change on next return.
  const refetchSummary = async (): Promise<void> => {
    try {
      const summary = await apiClient.getAuctionSummary();
      state.summary = summary;
      playerData.auctionSummary = summary;
      renderSummaryLine(el, summary);
    } catch { /* silent — non-critical */ }
  };

  // Kick off listings + summary + notifications in parallel on mount.
  void (async () => {
    try {
      const [{ listings, pendingClaims, gems: freshGems }, summary, { notifications }] = await Promise.all([
        apiClient.listAuctions(),
        apiClient.getAuctionSummary(),
        apiClient.getAuctionNotifications(),
      ]);
      // The list endpoint runs the sweep; any escrow refund it applied
      // (winner overage, canceled-auction refund) is reflected in this
      // gems value. Sync client-side before render so the header
      // .player-gems readout matches the server.
      onGemsChanged(freshGems);
      // Custom cards being auctioned won't necessarily be in the local
      // catalog (system-listed cards the player has never touched).
      // Fetch defs on demand so the tiles can render the full card —
      // both active listings AND pending claims need their card defs
      // resolved before the "To claim" filter renders.
      const allCardIds = [...listings, ...pendingClaims].map(l => l.cardId);
      await mergeMissingCardsByIds(catalog, allCardIds);
      state.listings = listings;
      state.pendingClaims = pendingClaims;
      state.summary = summary;
      playerData.auctionSummary = summary;
      refreshFilterUniverse();
      renderFilters();
      renderSortBar();
      renderTiles();
      renderSummaryLine(el, summary);

      // Apply local-state side effects from notifications BEFORE
      // showing popups. Card-return events (unsold + canceled-seller)
      // put a card back in the player's collection server-side; the
      // client mirrors that here so the deck editor sees the card
      // without a full refresh. Doing this pre-popup means state is
      // consistent by the time the player closes the notice.
      applyNotificationSideEffects(notifications, playerData);

      // Drain notifications as popups. Sequenced (onDismissed → next) so
      // the player reads them one at a time rather than seeing them stack.
      // After the last one dismisses (or there were none), mark read and
      // refetch summary so the pip clears.
      if (notifications.length > 0) {
        showNextNotification(notifications, 0, () => {
          void apiClient.markAuctionNotificationsRead().then(refetchSummary);
        });
      }
    } catch (err) {
      console.error('Failed to load auctions', err);
      const tiles = el.querySelector('#auction-tiles')!;
      tiles.innerHTML = '<div class="auction-empty meta">Couldn\'t load auctions. Try again in a moment.</div>';
    }
  })();

  return (): void => {
    if (countdownTimer !== null) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
    document.removeEventListener('click', outsideClickHandler);
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function escapeAttr(s: string): string { return escapeHtml(s); }

/** Build a single tile: full card (same visual as deck-builder collection
 *  tiles) with an auction-info footer strip below. Clicking anywhere on
 *  the tile navigates to the auction detail. */
function buildTile(
  l: AuctionListing,
  catalog: Map<string, CardDef>,
  onSelect: (auctionId: string) => void,
): HTMLElement {
  const tile = document.createElement('div');
  tile.className = 'auction-tile';
  tile.dataset.auctionId = l.auctionId;

  const def = catalog.get(l.cardId);
  if (def) {
    // Same signature as the deck-builder tile-mode card: lazy image
    // loading, tooltip suppressed (the whole tile is a click target).
    const card = createCardElement(def, undefined, false, undefined, false, 0, true, true);
    tile.appendChild(card);
  } else {
    // Fallback: system-listed card whose def we couldn't resolve.
    const stub = document.createElement('div');
    stub.className = 'card auction-tile-stub';
    stub.textContent = l.cardName;
    tile.appendChild(stub);
  }

  // Chip cascade — highest-priority actionable state wins. "Selling"
  // sits below the bidder-side chips because a listing can't be
  // simultaneously your bid AND your sale (the server rejects bids on
  // your own listing), but sits ABOVE "System" so a player-listed
  // card the viewer put up shows as their own rather than generic.
  const chip = l.status === 'closed_sold'
    ? '<span class="auction-chip claim">To claim</span>'
    : l.myBidState === 'leading'
      ? '<span class="auction-chip leading">Leading</span>'
      : l.myBidState === 'outbid'
        ? '<span class="auction-chip outbid">Outbid</span>'
        : l.iAmSeller
          ? '<span class="auction-chip selling">Selling</span>'
          : l.isSystemSeller
            ? '<span class="auction-chip system">System</span>'
            : '';
  const endsMs = new Date(l.endsAt).getTime();
  const footer = document.createElement('div');
  footer.className = 'auction-tile-footer';
  // Grey the price when it's my own listing and no one has bid yet —
  // the number is the reserve, not an active offer, and the seller
  // needs a clear visual cue that their card hasn't attracted a bid.
  // For system / other-player listings, the price still reads as the
  // current asking amount (buyers evaluate reserve + all bids the
  // same way), so leave those in the normal style.
  const showAsReserveOnly = l.iAmSeller && !l.hasBids;
  const priceClass = showAsReserveOnly ? 'auction-tile-price no-bids' : 'auction-tile-price';
  const priceTitle = showAsReserveOnly ? ' title="Reserve — no bids yet"' : '';
  footer.innerHTML = `
    <div class="auction-tile-footer-row">
      <span class="${priceClass}"${priceTitle}>${l.visiblePrice} 💎</span>
      <span class="auction-tile-countdown" data-ends-at="${endsMs}">${formatCountdown(endsMs)}</span>
    </div>
    ${chip ? `<div class="auction-tile-footer-row">${chip}</div>` : ''}
  `;
  tile.appendChild(footer);

  tile.addEventListener('click', () => onSelect(l.auctionId));
  return tile;
}

function renderSummaryLine(el: HTMLElement, summary: AuctionMenuSummary | null): void {
  const line = el.querySelector('.auction-summary-line')!;
  if (!summary) { line.textContent = ''; return; }
  const parts: string[] = [];
  if (summary.pendingClaims > 0) parts.push(`<strong>${summary.pendingClaims} to claim</strong>`);
  if (summary.activeBids > 0) parts.push(`${summary.activeBids} active bid${summary.activeBids === 1 ? '' : 's'}`);
  if (summary.activeSales > 0) parts.push(`${summary.activeSales} sale${summary.activeSales === 1 ? '' : 's'}`);
  line.innerHTML = parts.length ? parts.join(' &middot; ') : 'No active bids or sales.';
}

/** Mirror server-side collection changes into the client's local
 *  state, driven off unread notifications. The server has already
 *  applied the underlying effect (returned card, refunded ticket)
 *  before emitting the notification — this just keeps the client's
 *  in-memory PlayerData in sync so the deck editor, menu tile, and
 *  collection view all render correctly without a full page reload.
 *
 *  - `unsold`: seller's card returned. Bump collection.
 *  - `canceled` with role='seller': same, plus a ticket refund.
 *
 *  `won` and `sold` don't need mirroring: `won` requires an explicit
 *  claim before the card lands in the collection (handled by the
 *  claim endpoint's own state update); `sold` doesn't touch the
 *  seller's collection at all (gems, not a card). */
function applyNotificationSideEffects(
  notifications: AuctionNotification[],
  playerData: PlayerData,
): void {
  const now = new Date().toISOString();
  for (const n of notifications) {
    const p = n.payload as { cardId?: string; role?: string };
    const cardId = p.cardId;
    if (!cardId) continue;

    if (n.eventType === 'unsold') {
      playerData.collection[cardId] = (playerData.collection[cardId] ?? 0) + 1;
      playerData.collectionAcquiredAt[cardId] = now;
    } else if (n.eventType === 'canceled' && p.role === 'seller') {
      playerData.collection[cardId] = (playerData.collection[cardId] ?? 0) + 1;
      playerData.collectionAcquiredAt[cardId] = now;
      // Ticket refund is also part of a seller-side cancellation.
      const ticket = playerData.tickets['auction-listing'];
      playerData.tickets['auction-listing'] = {
        count: (ticket?.count ?? 0) + 1,
        lastUsed: ticket?.lastUsed ?? null,
      };
    }
  }
}

/** Sequentially display the queued notifications as `showNotice`
 *  popups, each chained via onDismissed to the next. */
function showNextNotification(
  list: AuctionNotification[],
  idx: number,
  onDone: () => void,
): void {
  if (idx >= list.length) { onDone(); return; }
  const n = list[idx];
  const p = n.payload as Record<string, unknown>;
  const cardName = String(p.cardName ?? 'a card');
  let title = 'Auction';
  let message = '';
  switch (n.eventType) {
    case 'won':
      title = 'You won!';
      message = `You won the auction for ${cardName}! Claim your card from the auction house.`;
      break;
    case 'outbid':
      title = 'Outbid';
      message = `You were outbid on ${cardName}. Your gems were refunded.`;
      break;
    case 'sold':
      title = 'Sold!';
      message = `Congratulations, you sold ${cardName} to ${String(p.buyerUsername ?? 'a player')}! You got ${String(p.gemsEarned ?? '?')} gems!`;
      break;
    case 'unsold':
      title = 'Unsold';
      message = `No one bid on ${cardName} so it has been returned to your collection.`;
      break;
    case 'canceled': {
      const role = String(p.role ?? '');
      if (role === 'seller') {
        title = 'Auction canceled';
        message = `Your auction for ${cardName} was canceled. The card and listing ticket have been returned to you.`;
      } else {
        title = 'Auction canceled';
        const refunded = String(p.gemsRefunded ?? '');
        message = refunded
          ? `An auction you were leading (${cardName}) was canceled. Your ${refunded} gems have been refunded.`
          : `An auction you were leading (${cardName}) was canceled. Your gems have been refunded.`;
      }
      break;
    }
  }
  showNotice({
    title,
    message,
    onDismissed: () => showNextNotification(list, idx + 1, onDone),
  });
}

/** Modal picker for the sell flow. Renders the player's owned custom
 *  cards in a filterable grid (adapts the settings profile-picker
 *  pattern), with a footer showing the reserve preview once a card is
 *  picked. The profile card is filtered out — see the server-side
 *  guard in POST /api/auctions/list. Premade cards can't be
 *  auctioned, so those are filtered out too.
 *
 *  Returns nothing — the effect is a call to `onListed(cardId)` on
 *  successful listing. If the player cancels, nothing happens. */
interface AuctionSellPickerOpts {
  playerData: PlayerData;
  catalog: Map<string, CardDef>;
  onListed: (cardId: string) => void;
}

async function openAuctionSellPicker(opts: AuctionSellPickerOpts): Promise<void> {
  const { playerData, catalog, onListed } = opts;
  const summary = playerData.auctionSummary;

  // Build the eligible-card list: owned (collection count > 0), custom
  // (GUID id), and not the profile card. Sort alpha for stable ordering.
  const eligible: CardDef[] = [];
  for (const [id, count] of Object.entries(playerData.collection)) {
    if (count <= 0) continue;
    if (!isCustomCardId(id)) continue;
    if (id === playerData.profileCardId) continue;
    const def = catalog.get(id);
    if (!def) continue;
    eligible.push(def);
  }
  eligible.sort((a, b) => a.name.localeCompare(b.name));

  let selected: string | null = null;
  let filterText = '';

  const overlay = document.createElement('div');
  overlay.className = 'profile-picker-overlay auction-sell-overlay';
  overlay.innerHTML = `
    <div class="profile-picker-modal auction-sell-modal">
      <header class="profile-picker-header">
        <div class="auction-sell-title">
          <h2>Sell a Card</h2>
          <div class="meta">Once posted, the auction cannot be canceled.</div>
        </div>
        <input type="search" id="picker-filter" class="settings-filter" placeholder="Filter by name…" autocomplete="off" />
      </header>
      <div class="profile-carousel picker-carousel-grid" id="picker-grid"></div>
      <div class="auction-sell-preview meta" id="sell-preview"></div>
      <footer class="profile-picker-footer">
        <button type="button" class="menu-btn" id="picker-cancel">Cancel</button>
        <button type="button" class="menu-btn primary-blue auction-list-btn" id="picker-list" disabled>
          <span class="auction-list-btn-main">Add to Auction</span>
          <span class="auction-list-btn-cost">-1 🎟</span>
        </button>
      </footer>
    </div>
  `;
  document.body.appendChild(overlay);

  const filterInput = overlay.querySelector('#picker-filter') as HTMLInputElement;
  const gridEl = overlay.querySelector('#picker-grid') as HTMLElement;
  const previewEl = overlay.querySelector('#sell-preview') as HTMLElement;
  const cancelBtn = overlay.querySelector('#picker-cancel') as HTMLButtonElement;
  const listBtn = overlay.querySelector('#picker-list') as HTMLButtonElement;

  const updatePreview = (): void => {
    if (!selected) { previewEl.textContent = ''; listBtn.disabled = true; return; }
    listBtn.disabled = false;
    const def = catalog.get(selected);
    if (!def || !summary) { previewEl.textContent = ''; return; }
    const reserve = summary.reservePriceByRarity[def.rarity] ?? summary.reservePriceByRarity.common ?? 0;
    previewEl.innerHTML = `Reserve: <strong>${reserve} 💎</strong> &middot; runs ${summary.durationHours}h &middot; you receive ${summary.sellerPayoutPercent}% of the winning bid.`;
  };

  // Observer for lazy image loading. Mirrors the deck-editor's
  // `fillLazyGrid` pattern (which handles 1000+ card collections on
  // Safari reliably), plus a scroll-debounce so a fast flick to the
  // bottom of the grid doesn't queue thousands of image fetches that
  // Safari never re-prioritizes against the tiles actually visible
  // when the scroll settles.
  //
  //   - `root: gridEl` — the grid element IS the scroll container,
  //     matching root and viewport. Prior attempts used a nested
  //     wrapper as `root` which broke Safari.
  //   - `loading="lazy"` alone doesn't work here (Safari desktop
  //     ignores it for nested scroll containers), so we set img.src
  //     explicitly on intersect.
  //   - Scroll debounce: on intersect, schedule a timeout for
  //     HYDRATION_COOLDOWN_MS; hydrate only if the slot is still
  //     intersecting when the timeout fires. Tiles that flash through
  //     view during a fast scroll never trigger a fetch.
  //   - Initial dispatch bypasses the debounce: tiles visible when the
  //     modal opens hydrate right away, no artificial delay for the
  //     tiles the user is actually looking at.
  //   - No dehydration. Once an image is loaded we leave it; scrolling
  //     back doesn't refetch. Browser image cache + our 24h max-age
  //     handles the rest.
  let observer: IntersectionObserver | null = null;
  const HYDRATION_COOLDOWN_MS = 250;
  const pendingHydrations = new WeakMap<HTMLElement, number>();
  // Parallel set of live timeout IDs so we can clear them all at once
  // on filter change / modal close (WeakMap doesn't allow iteration).
  const pendingTimeoutIds = new Set<number>();
  let firstDispatch = true;

  const clearAllPending = (): void => {
    for (const id of pendingTimeoutIds) clearTimeout(id);
    pendingTimeoutIds.clear();
  };

  const hydrateSlot = (slot: HTMLElement): void => {
    const art = slot.querySelector('.profile-carousel-art') as HTMLElement | null;
    if (!art || art.querySelector('img')) return;   // already loaded
    const artUrl = slot.dataset.art;
    if (!artUrl) return;
    const img = document.createElement('img');
    img.src = artUrl;
    img.alt = slot.dataset.cardName ?? '';
    img.draggable = false;
    art.appendChild(img);
  };

  const cancelPendingHydration = (slot: HTMLElement): void => {
    const t = pendingHydrations.get(slot);
    if (t !== undefined) {
      clearTimeout(t);
      pendingHydrations.delete(slot);
      pendingTimeoutIds.delete(t);
    }
  };

  const schedulePendingHydration = (slot: HTMLElement): void => {
    if (pendingHydrations.has(slot)) return;
    const t = window.setTimeout(() => {
      pendingHydrations.delete(slot);
      pendingTimeoutIds.delete(t);
      hydrateSlot(slot);
    }, HYDRATION_COOLDOWN_MS);
    pendingHydrations.set(slot, t);
    pendingTimeoutIds.add(t);
  };

  const buildSlot = (def: CardDef): HTMLElement => {
    const tile = document.createElement('div');
    tile.className = 'profile-carousel-tile' + (def.id === selected ? ' is-current' : '');
    // Stash the art URL + name on the slot so hydrateSlot can attach
    // the img without needing a closure over `def`. Keeps hydrateSlot
    // free of per-def bindings so a single WeakMap or attribute reads
    // fine.
    if (def.art) tile.dataset.art = def.art;
    tile.dataset.cardName = def.name;

    const art = document.createElement('div');
    art.className = 'profile-carousel-art';
    if (!def.art) {
      art.classList.add('profile-carousel-art-fallback');
      art.textContent = def.name.split(/\s+/).map(w => w[0] ?? '').join('').slice(0, 3).toUpperCase();
    }
    tile.appendChild(art);
    const name = document.createElement('div');
    name.className = 'profile-carousel-name';
    name.textContent = def.name;
    tile.appendChild(name);
    tile.addEventListener('click', () => {
      // First tap selects the tile. Second tap on the same tile
      // opens the standard card detail modal — matches the settings
      // profile-picker interaction so players don't have to learn
      // two patterns.
      if (def.id === selected) {
        void import('./CardDetailView.js').then(m => m.showCardDetail(def.id, catalog));
        return;
      }
      selected = def.id;
      gridEl.querySelectorAll('.profile-carousel-tile.is-current').forEach(t => t.classList.remove('is-current'));
      tile.classList.add('is-current');
      updatePreview();
    });
    return tile;
  };

  const renderGrid = (): void => {
    observer?.disconnect();
    clearAllPending();
    gridEl.innerHTML = '';
    const filtered = filterText
      ? eligible.filter(d => d.name.toLowerCase().includes(filterText.toLowerCase()))
      : eligible;
    if (filtered.length === 0) {
      gridEl.innerHTML = '<div class="profile-carousel-empty">'
        + (eligible.length === 0
          ? 'You have no custom cards to sell. (Your profile card can\'t be auctioned.)'
          : 'No cards match your filter.')
        + '</div>';
      return;
    }
    // Build all slot DOM up front (lightweight — each is just a few
    // divs + a click handler). Images are NOT set yet — the observer
    // attaches them as slots enter view. This gives the correct
    // scrollbar geometry (bounded by full filtered length) while
    // keeping the network cost bounded by the scroll position.
    const frag = document.createDocumentFragment();
    for (const def of filtered) frag.appendChild(buildSlot(def));
    gridEl.appendChild(frag);

    firstDispatch = true;
    observer = new IntersectionObserver((entries) => {
      // First callback fires shortly after `observe()` with entries
      // for every observed target (its initial intersection state).
      // Skip the debounce on this pass — those tiles are what the
      // player is looking at right now.
      if (firstDispatch) {
        firstDispatch = false;
        for (const entry of entries) {
          if (entry.isIntersecting) hydrateSlot(entry.target as HTMLElement);
        }
        return;
      }
      for (const entry of entries) {
        const slot = entry.target as HTMLElement;
        if (entry.isIntersecting) schedulePendingHydration(slot);
        else cancelPendingHydration(slot);
      }
    }, { root: gridEl, rootMargin: '300px 0px' });
    for (const child of Array.from(gridEl.children)) observer.observe(child);
  };

  filterInput.addEventListener('input', () => {
    filterText = filterInput.value.trim();
    renderGrid();
  });

  // Push a history entry when the modal opens so Back (browser or the
  // sticky-header in-page button, which both fire history.back()) pops
  // this state and triggers our popstate handler to close the modal.
  // Without this, Back navigates away from the auction house entirely
  // and leaves the modal orphaned above the main menu. The URL isn't
  // changed — same `/auction` path — but the state marker lets us
  // detect a Back attempt while the modal is up. Preserve any existing
  // state fields (main.ts uses `{cardverse: true}`) so goBack's
  // ownership check still passes if this entry is later navigated
  // over via history.back().
  history.pushState(
    { ...(history.state ?? { cardverse: true }), auctionSellPicker: true },
    '',
  );

  let closed = false;
  const cleanup = (opts: { fromPopstate?: boolean } = {}): void => {
    if (closed) return;
    closed = true;
    observer?.disconnect();
    clearAllPending();
    overlay.remove();
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('popstate', onPopState);
    // If the user closed via Cancel / Add-to-Auction / Escape we
    // still hold the pushed history entry; consume it so a later
    // Back press doesn't just eat a stale marker. Popstate-triggered
    // closes already popped the entry themselves.
    if (!opts.fromPopstate) history.back();
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') cleanup(); };
  const onPopState = (): void => cleanup({ fromPopstate: true });
  document.addEventListener('keydown', onKey);
  window.addEventListener('popstate', onPopState);
  cancelBtn.addEventListener('click', () => cleanup());

  listBtn.addEventListener('click', async () => {
    if (!selected || listBtn.disabled) return;
    listBtn.disabled = true;
    const cardId = selected;
    const def = catalog.get(cardId);
    const cardName = def?.name ?? 'the card';
    try {
      const { reservePrice } = await listCardForAuction(playerData, cardId);
      cleanup();
      showNotice({
        title: 'Listed!',
        message: `${cardName} is up for auction with a reserve of ${reservePrice} gems. It will run for ${summary?.durationHours ?? 24} hours.`,
      });
      onListed(cardId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showNotice({ title: 'Listing failed', message: msg });
      listBtn.disabled = false;
    }
  });

  renderGrid();
}
